import type { Rarity, Sport } from '#src/__generated__/globalTypes.ts';
import { useConfigContext } from '#src/contexts/config/index.ts';
import { useFeatureFlags } from '#src/hooks/useFeatureFlags.tsx';
import { getRarityColor } from '#src/lib/colors.ts';
import type { Color } from '#src/style/types.ts';

import { SET_BASE } from './base/index.ts';
import { SET_COLORS } from './colors/index.ts';
import { SET_STELLAR } from './stellar/index.ts';
import { SET_WINTER } from './wntr/index.ts';

// ordered chronologically, oldest first
const SETS = [SET_WINTER, SET_STELLAR, SET_COLORS];

const findCardSetConfig = (slug?: string | null) =>
  SETS.find(s => s.slug === slug) ?? SET_BASE;

export const findLatestCardSetConfig = (sport: Sport) =>
  [...SETS]
    .reverse()
    .find(s => (s.supportedSports as readonly Sport[]).includes(sport)) ??
  SET_BASE;

export const useGetCardSetConfig = () => {
  const { cardSet } = useConfigContext();
  const {
    flags: { enableSummerCamp = false },
  } = useFeatureFlags();

  return (slug?: string | null) => {
    // currentSet can be undefined during transition, so we fallback to nextSet
    // to start showing the next set branding
    const setSlug = slug || cardSet?.currentSet?.slug || cardSet?.nextSet?.slug;

    const config = findCardSetConfig(setSlug);
    if (enableSummerCamp && config.slug === SET_COLORS.slug) {
      return { ...config, displayName: 'Summer camp' };
    }
    return config;
  };
};

export const getCollectionBackgroundUrl = (
  setSlug: Nullable<string>,
  teamSlug: Nullable<string>
): string | undefined => {
  const setConfig = findCardSetConfig(setSlug);
  if (!teamSlug || !('collections' in setConfig)) {
    return undefined;
  }
  return `${setConfig.collections.backgroundBaseUrl}${teamSlug}.webp`;
};

export const getCollectionBackground = (
  setSlug: Nullable<string>,
  teamSlug: Nullable<string>
) => {
  const url = getCollectionBackgroundUrl(setSlug, teamSlug);
  if (!url) {
    return undefined;
  }
  return {
    background: `url(${url}) top center / cover no-repeat`,
  };
};

export const useCommonGameSet = () => {
  const { cardSet } = useConfigContext();
  const setToDisplay = cardSet?.currentSet || cardSet?.nextSet;
  const getCardSetConfig = useGetCardSetConfig();

  return {
    ...getCardSetConfig(),
    endDate: setToDisplay?.endDate,
    name: setToDisplay?.name,
  };
};

export const useCurrentSetBackground = () => {
  const getCardSetConfig = useGetCardSetConfig();
  const currentSet = getCardSetConfig();
  return currentSet.background;
};

export function useGetSetBackground() {
  const getCardSetConfig = useGetCardSetConfig();
  return (setSlug?: string) => {
    const setConfig = getCardSetConfig(setSlug);
    return setConfig.background;
  };
}

export const useGetSetColor = () => {
  const getCardSetConfig = useGetCardSetConfig();
  return (setSlug: Nullable<string>) => {
    const setConfig = getCardSetConfig(setSlug);
    return setConfig.colors.main;
  };
};

export const useIsSpecialCardSet = () => {
  return (setSlug: Nullable<string>) => {
    return !!setSlug && setSlug !== 'common-base-set';
  };
};

export const useIsCurrentCardSet = () => {
  const commonGameSet = useCommonGameSet();
  return (setSlug: Nullable<string>) => {
    return !!setSlug && setSlug === commonGameSet.slug;
  };
};

const MIX_SHARD_COLOR: Color = 'var(--c-green-400)';

export const useGetRarityColor = () => {
  const getSetColor = useGetSetColor();
  return (
    rarity: Rarity,
    opts?: { opacity?: number; cardSet?: Nullable<string> }
  ): Color => {
    const { opacity = 1, cardSet } = opts || {};
    if (cardSet) {
      return getSetColor(cardSet);
    }
    return getRarityColor(rarity, { opacity });
  };
};

export const useGetRarityShardColor = () => {
  const getRarityColorWithCardSet = useGetRarityColor();
  return (rarity: Nullable<Rarity>, cardSet: Nullable<string>) => {
    if (rarity) {
      return getRarityColorWithCardSet(rarity, { cardSet });
    }
    return MIX_SHARD_COLOR;
  };
};

export const useSetLogoImage = () => {
  const getCardSetConfig = useGetCardSetConfig();
  return (slug: string | undefined) => {
    const setConfig = getCardSetConfig(slug);
    return setConfig.visual.brand.logoImage;
  };
};
